const DDEX_STORE_ID_TO_SLUG = {
  1: "youtube",
  3: "youtube",
  6: "beatport",
  8: "traxsource",
  9: "spotify",
  17: "amazon-music",
  19: "deezer",
  21: "apple-music",
  78: "tidal",
};

const DEFAULT_DISTRIBUTED_STORE_SLUGS = [
  "spotify",
  "apple-music",
  "amazon-music",
  "deezer",
  "tidal",
  "youtube",
  "beatport",
  "traxsource",
];

const STORE_DISPLAY_ORDER = DEFAULT_DISTRIBUTED_STORE_SLUGS;

function seededRandom(seed) {
  let state = Math.abs(Number(seed) || 1);
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

function slugify(value) {
  return String(value || "release")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "release";
}

function titleSlug(value) {
  return String(value || "Release")
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .slice(0, 50) || "Release";
}

function randomFromChars(rand, length, chars) {
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += chars[Math.floor(rand() * chars.length)];
  }
  return out;
}

function randomDigits(rand, length) {
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += String(Math.floor(rand() * 10));
  }
  if (out.charAt(0) === "0") {
    out = String(1 + Math.floor(rand() * 9)) + out.slice(1);
  }
  return out;
}

function buildUrlContext(release, storeSlug) {
  const releaseId = Number(release?.id) || 0;
  const seed = `${releaseId}:${storeSlug}:${release?.ean || ""}:${release?.name || ""}`;
  const rand = seededRandom(seed.split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0));

  return {
    slug: slugify(release?.name),
    titleSlug: titleSlug(release?.name),
    numeric7: randomDigits(rand, 7),
    numeric9: randomDigits(rand, 9),
    numericId: randomDigits(rand, 10),
    albumId22: randomFromChars(
      rand,
      22,
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
    ),
    alphanum10: randomFromChars(
      rand,
      10,
      "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
    ),
    ytToken27: randomFromChars(
      rand,
      27,
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_-",
    ),
  };
}

function buildStoreUrl(storeSlug, release) {
  const ctx = buildUrlContext(release, storeSlug);

  switch (storeSlug) {
    case "spotify":
      return `https://open.spotify.com/album/${ctx.albumId22}`;
    case "apple-music":
      return `https://music.apple.com/us/album/${ctx.slug}/${ctx.numericId}`;
    case "amazon-music":
      return `https://music.amazon.com/albums/B0${ctx.alphanum10}`;
    case "deezer":
      return `https://www.deezer.com/album/${ctx.numeric9}`;
    case "beatport":
      return `https://www.beatport.com/release/${ctx.slug}/${ctx.numeric7}`;
    case "traxsource":
      return `http://www.traxsource.com/title/${ctx.numeric7}/${ctx.titleSlug}`;
    case "tidal":
      return `https://store.tidal.com/ch/album/${ctx.numeric9}`;
    case "youtube":
    case "youtube-music":
      return `https://music.youtube.com/playlist?list=OLAK5uy_${ctx.ytToken27}`;
    default:
      return null;
  }
}

function extractStoreId(store) {
  if (store == null) {
    return null;
  }
  if (typeof store === "number" || typeof store === "string") {
    return Number(store);
  }
  if (typeof store === "object" && store.id != null) {
    return Number(store.id);
  }
  return null;
}

export function getLinkableStoreSlugsForRelease(release) {
  const storeIds = Array.isArray(release?.stores) ? release.stores : [];
  const slugs = storeIds
    .map(extractStoreId)
    .filter(storeId => storeId != null && !Number.isNaN(storeId))
    .map(storeId => DDEX_STORE_ID_TO_SLUG[storeId])
    .filter(Boolean);

  const uniqueSlugs = Array.from(new Set(slugs.length ? slugs : DEFAULT_DISTRIBUTED_STORE_SLUGS));

  return uniqueSlugs.sort(
    (a, b) => STORE_DISPLAY_ORDER.indexOf(a) - STORE_DISPLAY_ORDER.indexOf(b),
  );
}

export function shouldSimulateReleaseStoreUrls(release) {
  if (!release) {
    return false;
  }
  if (release.status === "taken_down" || release.status === "offline") {
    return false;
  }
  return release.status === "distributed" || release.has_delivery_confirmations === true;
}

export function generateDemoReleaseStoreUrls(release) {
  if (!shouldSimulateReleaseStoreUrls(release)) {
    return [];
  }

  return getLinkableStoreSlugsForRelease(release)
    .map(storeSlug => {
      const url = buildStoreUrl(storeSlug, release);
      if (!url) {
        return null;
      }
      return { store_name: storeSlug, url };
    })
    .filter(Boolean);
}

export function resolveDemoReleaseStoreUrls(release) {
  if (!shouldSimulateReleaseStoreUrls(release)) {
    return Array.isArray(release?.store_urls) ? release.store_urls : [];
  }
  return generateDemoReleaseStoreUrls(release);
}
